import { create } from 'zustand'
import { FloatingPlacement, usePanelRegistry } from '@/components/editor/panels/panel-registry'
import { EditorSize } from '@/components/editor/size-switch'

export const WIDGET_EDIT_PANEL = "widget-edit"

/**
 * `onWidget`: whether the settings go on top of the selected widget rather than in the "Edit widgets" panel.
 * `shown`: whether they are open there now; only opening them does that, not merely selecting a widget.
 */
export const useSettingsOnWidgetStore = create<{ onWidget: boolean, shown: boolean }>()(() => ({ onWidget: false, shown: false }))

export function hideSettingsOnWidget() {
  useSettingsOnWidgetStore.setState({ shown: false })
}

/**
 * Moves the widget settings to another size; the panel is closed while they show on the widget.
 * `placement` puts the floating window where the settings were dragged off the widget.
 */
export function resizeSettings(size: EditorSize, placement?: FloatingPlacement) {
  const panels = usePanelRegistry.getState()
  const isFloating = panels.floatingPanels.includes(WIDGET_EDIT_PANEL)
  useSettingsOnWidgetStore.setState({ onWidget: size === "overlay", shown: size === "overlay" })
  switch (size) {
    case "overlay":
      panels.closePanel(WIDGET_EDIT_PANEL)
      if (isFloating) panels.dockPanel(WIDGET_EDIT_PANEL)
      return
    case "docked":
      if (isFloating) panels.dockPanel(WIDGET_EDIT_PANEL)
      return panels.openPanel(WIDGET_EDIT_PANEL)
    case "floating":
      panels.closePanel(WIDGET_EDIT_PANEL)
      return panels.floatPanel(WIDGET_EDIT_PANEL, placement)
  }
}

// Opening the "Edit widgets" panel by any route (the rail, a chip, the widget tree) moves the settings
// into it, so they do not pop up on the widget once another panel replaces it
usePanelRegistry.subscribe((state, previous) => {
  const panelShown = (st: typeof state) =>
    st.openPanels.has(WIDGET_EDIT_PANEL) || st.floatingPanels.includes(WIDGET_EDIT_PANEL)
  if (panelShown(state) && !panelShown(previous)) useSettingsOnWidgetStore.setState({ onWidget: false, shown: false })
})
